'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { signOut } from 'next-auth/react'
import { FieldGrid, FieldGroup, FieldLabel, PrimaryButton, TextField } from '@/app/FormFields'

export default function OnboardingPage() {
  const router = useRouter()
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      const res = await fetch('/api/users/me/name', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firstName: firstName.trim(), lastName: lastName.trim() }),
      })
      if (!res.ok) throw new Error('Failed to save name')
      router.push('/')
      router.refresh()
    } catch {
      setError('Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-zinc-50 px-4 text-zinc-950 dark:bg-zinc-950 dark:text-zinc-100">
      <div className="w-full max-w-sm rounded-lg border border-zinc-200 bg-white p-8 shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
        <h1 className="mb-1 text-xl font-semibold text-zinc-950 dark:text-white">One last thing</h1>
        <p className="mb-6 text-sm text-zinc-600 dark:text-zinc-400">
          What should we call you? Your name is shown on tickets and responses.
          If you signed in with the wrong address,{' '}
          <button
            onClick={() => signOut({ callbackUrl: '/login' })}
            className="font-medium text-sky-700 underline underline-offset-2 hover:text-sky-800 dark:text-sky-400 dark:hover:text-sky-300"
          >
            sign out and try again
          </button>
          .
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <FieldGrid className="grid-cols-2">
            <FieldGroup>
              <FieldLabel htmlFor="firstName">First name</FieldLabel>
              <TextField
                id="firstName"
                type="text"
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Jane"
                autoFocus
              />
            </FieldGroup>
            <FieldGroup>
              <FieldLabel htmlFor="lastName">Last name</FieldLabel>
              <TextField
                id="lastName"
                type="text"
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Smith"
              />
            </FieldGroup>
          </FieldGrid>
          {error ? <p className="text-sm text-red-700 dark:text-red-300">{error}</p> : null}
          <PrimaryButton
            type="submit"
            disabled={loading}
            className="w-full"
          >
            {loading ? 'Saving…' : 'Continue'}
          </PrimaryButton>
        </form>
      </div>
    </main>
  )
}
